/**
 * RefZone Reader Backpack — a favorites shelf for open readers.
 * Items point back at a publication so the floating reader can be restored.
 */

const STORAGE_PREFIX = 'refreps.readerBackpack'
export const FAVORITE_PROMPT_SUPPRESS_KEY = 'backpack.favoriteSavedPrompt.suppress'
/** Pointer may fall this far outside the drop target and still count. */
export const BACKPACK_DROP_PAD = 48

export function backpackStorageKey (userId) {
  return `${STORAGE_PREFIX}.${userId || 'anonymous'}`
}

/** Identity key — adding the same item again updates in place. */
export function backpackItemKey (sourceType, id) {
  return `${sourceType || 'publication'}:${id ?? ''}`
}

/** @param {Record<string, any>} publication */
export function buildPublicationBackpackItem (publication) {
  if (!publication?.id) return null
  const image = String(publication.image || publication.cover || '').trim()
  return {
    id: String(publication.id),
    sourceType: 'publication',
    materialType: 'nfhs-publications',
    title: publication.title || 'Untitled publication',
    image,
    label: 'Publication',
    payload: publication,
    sport: publication.sportLabel || publication.sport || '',
    state: publication.state || '',
    savedAt: Date.now(),
  }
}

export function loadBackpackItems (userId) {
  try {
    const raw = window.localStorage.getItem(backpackStorageKey(userId))
    const parsed = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

export function saveBackpackItems (userId, items) {
  try {
    window.localStorage.setItem(
      backpackStorageKey(userId),
      JSON.stringify(Array.isArray(items) ? items : [])
    )
  } catch {
    // Storage unavailable (private mode / quota) — backpack stays in memory.
  }
}

export function isFavoritePromptSuppressed () {
  try {
    return window.localStorage.getItem(FAVORITE_PROMPT_SUPPRESS_KEY) === '1'
  } catch {
    return false
  }
}

export function suppressFavoritePrompt () {
  try {
    window.localStorage.setItem(FAVORITE_PROMPT_SUPPRESS_KEY, '1')
  } catch {
    // Ignore — the prompt will simply show again next session.
  }
}

export function rectContainsWithPad (rect, x, y, pad = BACKPACK_DROP_PAD) {
  if (!rect) return false
  return (
    x >= rect.left - pad &&
    x <= rect.right + pad &&
    y >= rect.top - pad &&
    y <= rect.bottom + pad
  )
}

export function createDefaultReaderBackpackState () {
  return {
    items: [],
    loadedFor: null,
    isOpen: false,
    isDragging: false,
    dropHover: false,
    justCaught: false,
    dragItem: null,
    pointer: null,
    stageFrom: null,
    pendingOpen: null,
    savedPromptItem: null,
  }
}
